import Axios from 'axios'
import { baseURL } from '../config'
import { listSounds, saveSound, DEFAULT_SOUNDS } from '../utils/soundDb'
import { clearSession } from './sessionCookie'

Axios.defaults.withCredentials = true

let abortController

Axios.interceptors.request.use(
	function (config) {
		const token = localStorage.getItem('jwtToken')
		if (token) {
			config.headers.authorization = token
		}
		return config
	},
	function (error) {
		return Promise.reject(error)
	}
)

Axios.interceptors.response.use(
	function (response) {
		return response
	},
	function (error) {
		if (error.response?.status === 401) {
			clearSession()
			localStorage.removeItem('email')
			window.location = '/account/login' + window.location.search
		}
		return Promise.reject(error)
	}
)

export const deleteSearch = async searchId => {
	try {
		const result = await Axios.delete(`${baseURL}/api/search/${searchId}`)
		return result
	} catch (error) {
		console.error('Error deleting search:', error)
		throw error
	}
}

export const nearCity = async ({
	dropoffMaxDist,
	pickupMaxDist,
	equipType,
	dropLat,
	dropLng,
	pickupLng,
	pickupLat,
	pickupLocations = [],
	dropoffLocations = [],
	price,
	sourceType,
	paginateSize,
	pricePerMile,
	deadHeadShortest,
	startDateNearest,
	loadsNewest,
	stopCount,
	weight,
	distance,
	maxdistance,
	limited = true,
	ignoreLoadOut,
	lastSeen = 1000,
	bobtail,
	isAIEnabled,
	loadType,
	workType,
	certRequired,
	sortBy,
	setPaymentRequired,
	driverType,
	carrierGrades,
	destinationRegions = [],
	destinationStates = [],
	pickupDateFrom,
	deliveryDateTo,
	_mc,
}) => {
	setPaymentRequired(false)

	weight = Number(weight)
	stopCount = Number(stopCount)

	if (abortController) {
		abortController.abort()
	}

	abortController = new AbortController()

	const dropOff = dropoffLocations?.length > 0
		? JSON.stringify(dropoffLocations.map(loc => ({
				lat: loc.lat,
				lng: loc.lng,
				maxDist: dropoffMaxDist || 100
		  })))
		: (dropLat && dropLng
			? JSON.stringify([{
					lat: dropLat,
					lng: dropLng,
					maxDist: dropoffMaxDist || 100,
			  }])
			: null)

	const pickup = pickupLocations?.length > 0
		? JSON.stringify(pickupLocations.map(loc => ({
				lat: loc.lat,
				lng: loc.lng,
				maxDist: pickupMaxDist || 50
		  })))
		: (pickupLat && pickupLng
			? JSON.stringify([{
					lat: pickupLat,
					lng: pickupLng,
					maxDist: pickupMaxDist || 50,
			  }])
			: null)


	let equip =
		!equipType || equipType === 'All Equipments' || equipType === 'Any'
			? null
			: JSON.stringify([equipType])


	const carrierGradesParam = carrierGrades?.length
		? JSON.stringify(carrierGrades)
		: null

	const destinationRegionsParam = destinationRegions?.length
		? JSON.stringify(destinationRegions)
		: null

	let result

	let sortType = ''
	if (sortBy === 'ppm') sortType = { pricePerMile: -1 }
	else if (sortBy === 'newest') sortType = { lastSeen: -1 }
	else if (sortBy === 'pickupDate') sortType = { pickupDate: 1 }
	else if (sortBy === 'payout') sortType = { price: -1 }
	else sortType = { pricePerMile: -1 }

	const stringSortBy = JSON.stringify(sortType)

	try {
		result = await Axios.get(
			`${baseURL}/api/loads?${dropOff ? 'dropOff=' + dropOff : ''}${
				pickup ? '&pickup=' + pickup : ''
			}&price=${price ? price : 0}&pricePerMile=${
				pricePerMile >= 0 &&
				pricePerMile !== true &&
				pricePerMile !== false &&
				pricePerMile !== null
					? pricePerMile
					: 0
			}&stopCount=${stopCount || 0}&weight=${
				weight || 0
			}&sortBy=${stringSortBy}&limited=${limited}&equipments=${equip}&paginate={"size":"50","number":"${
				paginateSize || 1
			}"}&limitedLoads=${!pickup ? 1 : 0}&distance=${
				distance ? distance : ''
			}&maxdistance=${maxdistance ? maxdistance : ''}&bobtail=${
				bobtail ? 'bobtail' : false
			}&lastSeen=${lastSeen}&driverType=${driverType || ''}&isAIEnabled=${
				isAIEnabled ? 'true' : 'false'
			}${loadType ? '&loadType=' + loadType : ''}${workType ? '&workType=' + workType : ''}${certRequired ? '&certRequired=' + certRequired : ''}${carrierGradesParam ? '&carrierGrades=' + carrierGradesParam : ''}${destinationRegionsParam ? '&destinationRegions=' + destinationRegionsParam : ''}${destinationStates?.length ? '&destinationState=' + JSON.stringify(destinationStates) : ''}${pickupDateFrom ? '&pickupDateFrom=' + pickupDateFrom : ''}${deliveryDateTo ? '&deliveryDateTo=' + deliveryDateTo : ''}${_mc === 1 ? '&_mc=1' : ''}`,
			{
				signal: abortController.signal,
				...(typeof _mc !== 'undefined' && _mc === 1 && {
					headers: {
						'X-Req-Meta': 't1'
					}
				})
			}
		)

		if (typeof result.data !== 'object' || result.data === null) {
			throw new Error(
				`Loads request returned ${result.headers?.['content-type'] || 'an unknown type'} instead of JSON`
			)
		}

		if (result.data?.message === 'Failed to authenticate the token!') {
			clearSession()
			window.location = '/'
			return window.location.reload()
		}

		setPaymentRequired(false)

		return result.data
	} catch (err) {
		if (err.message.includes('401') || err.response?.status === 401) {
			clearSession()
			localStorage.removeItem('email')
			window.location = '/account/login' + window.location.search
			return
		}

		if (Axios.isAxiosError(err) && err.response?.status === 402) {
			setPaymentRequired(true)
		}

		if (Axios.isAxiosError(err) && err.response?.status === 400) {
			const errorMessage = err.response?.data?.error || 'Invalid search parameters'
			alert(errorMessage)
			return { error: errorMessage, data: [] }
		}

		throw err
	}
}

export const loadAnalytics = async (coordinates, equipmentType) => {
	try {
		const res = await Axios.get(
			`${baseURL}/api/load/analytics?coordinates=${coordinates}&equipmentType=${equipmentType}`
		)

		return res
	} catch (err) {
		console.log(err)
	}
}

export const getLoadPriceChanges = async loadId => {
	try {
		const res = await Axios.get(`${baseURL}/api/load/price-changes?id=${loadId}`)
		return res.data
	} catch (err) {
		console.log(err)
		return { priceChanges: [] }
	}
}

export const getFacilityStats = async (facilityCode, period = '7d') => {
	const res = await Axios.get(`${baseURL}/api/facility-stats?code=${encodeURIComponent(facilityCode)}&period=${period}`)
	return res.data
}

export const getAllFacilityReviews = async (facilityCode, page = 1, limit = 10) => {
	const res = await Axios.get(`${baseURL}/api/facility-reviews?code=${encodeURIComponent(facilityCode)}&page=${page}&limit=${limit}`)
	return res.data
}

export const submitFacilityReview = async (facilityCode, rating, text, isAnonymous, isEdit = false) => {
	const res = await Axios.post(`${baseURL}/api/facility-review`, { code: facilityCode, rating, text, isAnonymous, isEdit })
	return res.data
}

export const deleteFacilityReview = async facilityCode => {
	const res = await Axios.delete(`${baseURL}/api/facility-review?code=${encodeURIComponent(facilityCode)}`)
	return res.data
}

export const getFacilityNearby = async facilityCode => {
	const res = await Axios.get(`${baseURL}/api/facility-nearby?code=${encodeURIComponent(facilityCode)}`)
	return res.data
}

export const getFacilityLoadActivity = async (facilityCode, period = '24h') => {
	const res = await Axios.get(`${baseURL}/api/facility-load-activity?code=${encodeURIComponent(facilityCode)}&period=${period}`)
	return res.data
}

let fetchDefaultSoundsInflight = null

export const fetchDefaultSounds = () => {
	if (fetchDefaultSoundsInflight) return fetchDefaultSoundsInflight
	fetchDefaultSoundsInflight = (async () => {
		const have = new Set((await listSounds()).map(s => s.id))
		const missing = DEFAULT_SOUNDS.filter(d => !have.has(d.id))
		if (!missing.length) return

		await Promise.all(
			missing.map(async d => {
				try {
					const res = await Axios.get(
						`${baseURL}/api/audio-sound/${d.id}/file`,
						{ responseType: 'blob' }
					)
					await saveSound({ id: d.id, name: d.name, blob: res.data })
				} catch (err) {
					console.warn('fetchDefaultSounds: failed for', d.id, err.message)
				}
			})
		)
	})().catch(err => {
		fetchDefaultSoundsInflight = null
		console.warn('fetchDefaultSounds failed', err.message)
	})
	return fetchDefaultSoundsInflight
}

export const generateTelegramCode = async () => {
	const res = await Axios.get(`${baseURL}/api/user/generate-telegram-code`)
	return res.data?.code
}

export const trackError = async (error, element = 'unknown', source = 'website') => {
	try {
		const clickData = [{
			element,
			action: 'error',
			source,
			error: error.message || 'Unknown error',
			stack: error.stack || new Error().stack,
			timestamp: new Date().toISOString()
		}]

		await Axios.post(`${baseURL}/api/analytics/clicks`, { clicks: clickData })
	} catch (trackingError) {
		console.error('Failed to track error:', trackingError)
	}
}

export const createLoadShare = async loadId => {
	const res = await Axios.post(`${baseURL}/api/share/loads`, { loadId })
	return res.data
}

export const getSharedLoad = async token => {
	const res = await Axios.get(`${baseURL}/api/share/loads/${token}`)
	return res.data
}

// Email accounts: the mailboxes the DAT One side of the extension sends from,
// managed on the settings page. Routes from LoadLine's server port.
export const getEmailAccounts = async () => {
	const result = await Axios.get(`${baseURL}/api/email/accounts`)
	return result
}

export const requestEmailAccess = async ({ provider, readAccess = false }) => {
	const result = await Axios.post(`${baseURL}/api/user/request-email-access`, { provider, readAccess })
	return result
}

export const connectSmtpAccount = async smtpConfig => {
	const result = await Axios.post(`${baseURL}/api/user/connect-smtp`, smtpConfig)
	return result
}

export const reauthorizeEmailAccount = async ({ email, readAccess = false }) => {
	const result = await Axios.post(`${baseURL}/api/user/re-authorize-email`, { email, readAccess })
	return result
}

export const deleteEmailAccount = async accountId => {
	const result = await Axios.delete(`${baseURL}/api/user/email-account/${accountId}`)
	return result
}

export const saveAccountSignature = async (accountId, signatureData) => {
	const result = await Axios.put(`${baseURL}/api/user/email-account/${accountId}/signature`, { signatureData })
	return result
}

export const deleteAccountSignature = async accountId => {
	const result = await Axios.delete(`${baseURL}/api/user/email-account/${accountId}/signature`)
	return result
}

// Email templates, shared with the DAT One side of the extension.
export const getEmailTemplates = async () => {
	const result = await Axios.get(`${baseURL}/api/email/templates`)
	return result
}

export const createEmailTemplate = async template => {
	const result = await Axios.post(`${baseURL}/api/email/templates`, template)
	return result
}

export const updateEmailTemplate = async (templateId, template) => {
	const result = await Axios.put(`${baseURL}/api/email/templates/${templateId}`, template)
	return result
}

export const deleteEmailTemplate = async templateId => {
	const result = await Axios.delete(`${baseURL}/api/email/templates/${templateId}`)
	return result
}

export const requestAiCompletion = async text => {
	const result = await Axios.post(`${baseURL}/api/extension/completion`, { text })
	return result.data.completion
}
